import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject } from 'rxjs';

import { AuthService } from 'src/app/services/auth.service';
import { CardContent } from 'src/app/types/card-content';
import { environment } from 'src/environments/environment';

/**
 * component which serves two purposes:
 *
 * 1. shown login button for unauthorized users
 * 2. shown launch scope button, when user is authorized
 *
 * @class HomeComponent
 * @typedef {HomeComponent}
 * @implements {OnInit}
 */
@Component( {
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: false
} )
export class HomeComponent implements OnInit {
    authService = inject( AuthService );
    #cookieService = inject( CookieService );

    /** flags to control which modules should be shown */
    showCommunityMap = environment.optionalModules.showCommunityMap;
    showScopeCommunity = environment.optionalModules.showScopeCommunity;
    showExchangeCalculator = environment.optionalModules.showExchangeCalculator;

    /** flag if feed posts got loaded */
    hasPosts = computed( () => this.hasBlogItems() || this.hasReleaseItems() );
    hasBlogItems = signal( true );
    hasReleaseItems = signal( true );

    cardItems$ = new BehaviorSubject<CardContent[]>( [] );

    /** on init set content for static cards */
    ngOnInit(): void {
        this.setCardContent();
    }

    // show links to knowledege base depending on language //
    setCardContent() {
        const cookieExists: boolean = this.#cookieService.check( 'language' );
        let lang = 'en';

        if ( cookieExists ) {
            lang = this.#cookieService.get( 'language' );
        } else {
            lang = navigator.language;
        }

        let linkKnowledgeBase = '';
        switch ( lang ) {
            case 'de':
            case 'de-DE':
            case 'de-AT':
            case 'de-CH':
                linkKnowledgeBase = 'https://service.riege.com/de/knowledge';
                break;
            case 'es':
                linkKnowledgeBase = 'https://service.riege.com/es-mx/knowledge';
                break;
            default:
                linkKnowledgeBase = 'https://service.riege.com/en/knowledge';
        }

        const cards: CardContent[] = [
            {
                title: $localize`:@@status-card-headline:Status Page`,
                text: $localize`:@@status-card-description:Get the last information on planned maintenance and the current system status`,
                href: 'https://status.riege.com/',
                icon: 'traffic'
            },
            {
                title: $localize`:@@manual-card-headline:Knowledge base`,
                text: $localize`:@@manual-card-description:Everything you need to know about Scope and its features`,
                href: linkKnowledgeBase,
                icon: 'collections_bookmark'
            },
            {
                title: $localize`:@@tenants-card-headline:Scope Tenants`,
                href: 'tenants',
                icon: 'domain'
            },
            {
                title: $localize`:@@map-card-headline:Community Map`,
                text: $localize`:@@map-card-description:See all ports and our Scope agents world wide.`,
                href: this.showScopeCommunity ? 'scope-community/community-map' : 'community-map',
                image: 'assets/images/map.png'
            }
        ];

        this.cardItems$?.next( cards );
    }
}
